// Phase 12 — Activity Submissions Report (+dynamic columns per form, xlsx/pdf export)
// Phase 24B — Shared CommonFilterBar adopted (Zone/SubZone/User/Date).
import { todayIST } from "@/lib/istDate";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, Filter, FileSpreadsheet, FileText as FileTextIcon } from "lucide-react";
import { api, formatApiError, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import CommonFilterBar from "@/components/reports/CommonFilterBar";
import PageHeader from "@/components/PageHeader";

export default function ActivityReportPage() {
  const [forms, setForms] = useState([]);
  const [formId, setFormId] = useState("");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [dateFrom, setDateFrom] = useState(() => todayIST());
  const [dateTo, setDateTo] = useState(() => todayIST());
  const [zoneId, setZoneId] = useState("");
  const [subZoneId, setSubZoneId] = useState("");
  const [userId, setUserId] = useState("");
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/activity-forms?page_size=100");
        setForms(data.items || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  async function load() {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, page_size: 200 };
      if (formId) params.form_id = formId;
      if (category) params.category = category;
      if (status) params.status = status;
      if (zoneId) params.zone_id = zoneId;
      if (subZoneId) params.sub_zone_id = subZoneId;
      if (userId) params.user_id = userId;
      const { data } = await api.get("/reports/activity-submissions", { params });
      setRows(data.items || []);
      setCols(data.columns || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [formId, category, status, dateFrom, dateTo, zoneId, subZoneId, userId]);

  async function download(format) {
    const t = getToken();
    const params = new URLSearchParams();
    params.set("date_from", dateFrom); params.set("date_to", dateTo);
    params.set("format", format);
    if (formId) params.set("form_id", formId);
    if (category) params.set("category", category);
    if (status) params.set("status", status);
    const url = `${API_BASE}/reports/activity-submissions/export?${params.toString()}`;
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `activity_submissions_${dateFrom}_to_${dateTo}.${format}`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { toast.error(String(e)); }
  }

  return (
    <div className="max-w-7xl mx-auto p-4" data-testid="activity-report-page">
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Activity" }]}
        title="Activity report"
        subtitle="All field activity submissions filtered by form, zone and user."
        favorite={{ key: "reports.activity", type: "screen", label: "Activity report", route: "/reports/activity-submissions" }}
        testid="activityreportpage-page-header"
      />

      <div className="mb-4">
        <h1 className="text-2xl font-semibold">Activity Submissions Report</h1>
        <p className="text-sm text-neutral-500">Dynamic columns per form when filtered by a single form.</p>
      </div>

      {/* Phase 24B — shared filter bar (Zone → SubZone cascading + User + Date range) */}
      <Card className="border-neutral-200 rounded-2xl mb-3">
        <CardContent className="p-3">
          <CommonFilterBar
            value={{ zone_id: zoneId, sub_zone_id: subZoneId, user_id: userId,
                       date_from: dateFrom, date_to: dateTo }}
            onChange={(f) => {
              setZoneId(f.zone_id || ""); setSubZoneId(f.sub_zone_id || "");
              setUserId(f.user_id || "");
              if (f.date_from !== undefined) setDateFrom(f.date_from || "");
              if (f.date_to !== undefined) setDateTo(f.date_to || "");
            }}
            showZone showSubZone showUser showDateRange
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <div>
          <Label>Form</Label>
          <Select value={formId || "__all__"} onValueChange={(v) => setFormId(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="rpt-form"><SelectValue placeholder="All forms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All forms</SelectItem>
              {forms.map(f => <SelectItem key={f.id} value={f.id}>{f.code} · {f.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Category</Label>
          <Select value={category || "__all__"} onValueChange={(v) => setCategory(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="rpt-category"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any category</SelectItem>
              <SelectItem value="general">General</SelectItem>
              <SelectItem value="btl">BTL</SelectItem>
              <SelectItem value="display">Display</SelectItem>
              <SelectItem value="market_survey">Market Survey</SelectItem>
              <SelectItem value="distributor_admin">Distributor Admin</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Status</Label>
          <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
            <SelectTrigger data-testid="rpt-status"><SelectValue placeholder="Any" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any status</SelectItem>
              <SelectItem value="submitted">Submitted</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <Button variant="outline" size="sm" onClick={() => download("xlsx")} data-testid="rpt-export-xlsx">
          <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
        </Button>
        <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="rpt-export-pdf">
          <FileTextIcon className="w-4 h-4 mr-1" /> PDF
        </Button>
        <div className="ml-auto text-sm text-neutral-500 self-center">
          {loading ? "Loading…" : `${rows.length} rows`}
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-neutral-600 text-left">
            <tr>
              {cols.map(c => (
                <th key={c.key} className="px-3 py-2 font-medium whitespace-nowrap">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t">
                {cols.map(c => (
                  <td key={c.key} className="px-3 py-2 whitespace-nowrap max-w-[220px] truncate"
                      title={String(r[c.key] ?? "")}>
                    {formatCell(r[c.key], c)}
                  </td>
                ))}
              </tr>
            ))}
            {rows.length === 0 && !loading && (
              <tr><td colSpan={cols.length} className="px-3 py-8 text-center text-neutral-400">No rows</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function formatCell(v, col) {
  if (v == null || v === "") return "—";
  if (col.key === "submission_ts") return new Date(v).toLocaleString();
  if (col.key === "status") {
    const styles = {
      verified: "bg-emerald-50 text-emerald-700 border-emerald-200",
      rejected: "bg-red-50 text-red-700 border-red-200",
      submitted: "bg-amber-50 text-amber-700 border-amber-200",
    };
    return <Badge variant="outline" className={styles[v] || ""}>{v}</Badge>;
  }
  return String(v);
}
